import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBootstrapQuery } from '~/layouts/AppLayout/useBootstrapQuery';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import SignOutButton from '../SignOutButton';

function SchoolSelector() {
  const navigate = useNavigate();
  const { loading, error, data } = useBootstrapQuery();
  const [selectedSchool, setSelectedSchool] = useState('');

  const accessibleSchools = useMemo(
    () => data?.currentUser?.appPortal?.accessibleSchools || [],
    [data?.currentUser?.appPortal?.accessibleSchools]
  );

  useEffect(() => {
    if (accessibleSchools.length === 1) {
      navigate(`/app/${accessibleSchools[0].school.slug}`);
    }
  }, [accessibleSchools, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedSchool) {
      const accessibleSchoolEntry = accessibleSchools.find(
        (as: { school: { id: string } }) => as.school.id === selectedSchool
      );
      if (accessibleSchoolEntry) {
        localStorage.setItem('solastack.selectedSchoolId', accessibleSchoolEntry.school.id);
        if (accessibleSchoolEntry.roles.length > 0) {
          localStorage.setItem('solastack.selectedRole', accessibleSchoolEntry.roles[0]);
        }
        navigate(`/app/${accessibleSchoolEntry.school.slug}`);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p>Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    if (error.message?.includes('authenticate') || (error.networkError as { statusCode?: number } | null)?.statusCode === 401) {
      window.location.href = '/users/sign_in';
      return null;
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>{error.message}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (!data?.currentUser) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Not Authenticated</CardTitle>
            <CardDescription>Please log in to continue.</CardDescription>
          </CardHeader>
          <CardContent>
            <a
              href="/users/sign_in"
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Sign In
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accessibleSchools.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>No Schools</CardTitle>
            <CardDescription>You do not have access to any schools.</CardDescription>
          </CardHeader>
          <CardContent>
            <SignOutButton />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Select School</CardTitle>
          <CardDescription>Choose a school to continue</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="school-select">Choose a school:</Label>
              <Select
                value={selectedSchool}
                onValueChange={setSelectedSchool}
                required
              >
                <SelectTrigger id="school-select">
                  <SelectValue placeholder="-- Select a school --" />
                </SelectTrigger>
                <SelectContent>
                  {accessibleSchools.map((accessibleSchoolEntry: { school: { id: string; name: string } }) => (
                    <SelectItem key={accessibleSchoolEntry.school.id} value={accessibleSchoolEntry.school.id}>
                      {accessibleSchoolEntry.school.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Button type="submit" className="w-full">Continue</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default SchoolSelector;
